import {
	computed,
	ref,
	Ref,
	SetupContext,
	watch,
} from '@vue/composition-api';
import deepEqual from 'deep-equal';
import { SelectBoxChoice } from '@front-app/components/inputs/SelectBoxModel';
import { sortAlphabeticalDesc } from '@front-app/util/sort';

export interface CheckboxConfig <T extends string>{
	choiceKeys: Ref<T[]>;
	initialChoices: Ref<T[]>;
	labels?: Record<T, string>;
	labelFn?: LabelFn<T>;
	sortFns?: SortFn<T>[];
}

export type SortFn<T extends string> = (
	a: T,
	b: T,
	selectedChoices: T[],
	getLabel: LabelFn<T>,
) => number;

export type LabelFn<T extends string> = (value: T) => string;

export interface UseCheckboxes<T extends string> {
	choices: Ref<SelectBoxChoice[]>;
	selectedChoices: Ref<T[]>;
	toggle(v: T): void;
}

export const useCheckboxes = <T extends string>(
	ctx: SetupContext,
	config: CheckboxConfig<T>,
): UseCheckboxes<T> => {
	const {
		choiceKeys,
		labels = {} as Record<T, string>,
		labelFn = getDefaultLabelFn(ctx, labels),
		sortFns = [noSort],
	} = config;
	// Weird TS inference issue here, have to use "as" :(
	const selectedChoices = ref([]) as Ref<T[]>;

	watch(config.initialChoices, (curr, prev) => {
		if (!deepEqual(curr, selectedChoices.value) && !deepEqual(curr, prev)) {
			selectedChoices.value = [...curr];
		}
	}, { immediate: true });

	const sorter = getSorter(sortFns);
	const choices: Ref<SelectBoxChoice[]> = computed(() => choiceKeys
		.value
		.sort((a, b) => sorter(a, b, selectedChoices.value, labelFn))
		.map((key) => ({
			key,
			label: labelFn(key),
		})));

	const toggle = (newVal: T) => {
		const choicesVal = selectedChoices.value;
		if (choicesVal.includes(newVal)) {
			selectedChoices.value = choicesVal.filter((cat) => cat !== newVal);
		} else {
			selectedChoices.value = [...selectedChoices.value, newVal];
		}
	};
	return {
		choices,
		selectedChoices,
		toggle,
	};
};

function getDefaultLabelFn<T extends string>(ctx: SetupContext, labels: Record<T, string>) {
	return (key: T) => ctx.root.$t(labels[key]) as string;
}

function noSort(): number {
	return 0;
}

export function sortBySelectedFirst<T extends string>(a: T, b: T, selectedChoices: T[]): number {
	const hasA = selectedChoices.includes(a);
	const hasB = selectedChoices.includes(b);
	if ((hasA && hasB) || (!hasA && !hasB)) {
		return 0;
	}
	return hasA ? -1 : 1;
}

export function sortByAlphabeticalDesc<T extends string>(a: T, b: T, selectedChoices: T[], labelFn: LabelFn<T>): number {
	const nameA = labelFn(a);
	const nameB = labelFn(b);
	return sortAlphabeticalDesc(nameA, nameB);
}

function getSorter<T extends string>(sorters: SortFn<T>[]): SortFn<T> {
	return (a: T, b: T, selectedChoices: T[], labelFn: LabelFn<T>): number => {
		let val = 0;
		for (let i = 0; i < sorters.length; i++) {
			val = sorters[i](a, b, selectedChoices, labelFn);
			if (val !== 0) {
				return val;
			}
		}
		return val;
	};
}
